import React from "react";
import {useMutation, useSubscription} from "@apollo/client";
import {QUERY_SERVICE, QUERY_SERVICE_ARCHIVED, QUERY_SERVICE_GROUPS} from "../registryComponentsGQL.js";
import {Box} from "grommet";
import {DataGrid} from "@mui/x-data-grid";
import {Button as ButtonMui, Dialog, DialogTitle, DialogContent, DialogActions, TextField} from "@mui/material";
import {DELETE_SERVICE, UNARCHIVE_SERVICE, INSERT_SERVICE_GROUP, UPDATE_SERVICE_GROUP, DELETE_SERVICE_GROUP} from "./ServiceGQL.js";

function Servizi(props){
    const [deleteService] = useMutation(DELETE_SERVICE);
    const [unarchiveService] = useMutation(UNARCHIVE_SERVICE);
    const [insertServiceGroup] = useMutation(INSERT_SERVICE_GROUP);
    const [updateServiceGroup] = useMutation(UPDATE_SERVICE_GROUP);
    const [deleteServiceGroup] = useMutation(DELETE_SERVICE_GROUP);

    const [openDialog, setOpenDialog] = React.useState(false);
    const [selectedGroup, setSelectedGroup] = React.useState(null);
    const [groupName, setGroupName] = React.useState('');

    const { data: dataActive, loading: loadingActive } = useSubscription(
      QUERY_SERVICE,
    );

    const { data: dataArchived, loading: loadingArchived } = useSubscription(
      QUERY_SERVICE_ARCHIVED,
    );

    const { data: dataServiceGroups } = useSubscription(
      QUERY_SERVICE_GROUPS,
    );

    // Combina i servizi attivi e archiviati
    const combinedData = React.useMemo(() => {
      const activeServices = dataActive?.["clubmate_clubmateservice_clubmateservice"] || [];
      const archivedServices = dataArchived?.["clubmate_clubmateservice_clubmateservice"] || [];
      const serviceGroups = dataServiceGroups?.["clubmate_clubmateservice_clubmateservicegroup"] || [];

      // Crea una mappa dei gruppi per un accesso rapido
      const groupsMap = serviceGroups.reduce((acc, group) => {
        acc[group.id] = group.name;
        return acc;
      }, {});

      // Aggiungi il nome del gruppo ai servizi
      const servicesWithGroupName = [...activeServices, ...archivedServices].map(service => ({
        ...service,
        group_name: service.service_group_id ? groupsMap[service.service_group_id] : null
      }));

      return servicesWithGroupName;
    }, [dataActive, dataArchived, dataServiceGroups]);

    const columnsPack = [
      { field: 'name', headerName: 'Nome', width: 170 },
      { field: 'price', headerName: 'Prezzo', width: 170 },
      { field: 'group_name', headerName: 'Gruppo', width: 150 },
      { field: 'visible_for_partner', headerName: 'Partner', width: 170 },
      { field: 'entrance_service', headerName: 'Servizio di ingresso per non soci?', width: 250 },
      { field: 'request_entrance_for_use', headerName: "Richiede l'ingresso per essere utilizzato?", width: 300 },
      {
        field: 'actions',
        headerName: 'Azioni',
        width: 120,
        renderCell: (cellValues) => {
          // Se il servizio è archiviato, mostra il bottone Disarchivia
          if (cellValues.row.archive) {
            return (
              <ButtonMui
                variant="contained"
                color="success"
                onClick={(event) => {
                  let res = window.confirm("Sei sicuro di voler disarchiviare il servizio?");
                  if (res) {
                    unarchiveService({variables: { id: cellValues.row.id }});
                  }
                }}
              >
                Disarchivia
              </ButtonMui>
            );
          } else {
            // Se il servizio è attivo, mostra il bottone Modifica
            return (
              <ButtonMui
                variant="contained"
                color="warning"
                onClick={(event) => {
                  props?.setElementSel(cellValues.row)
                }}
              >
                Modifica
              </ButtonMui>
            );
          }
        },
      },
      {
        field: 'delete',
        headerName: 'Cancella',
        width: 120,
        renderCell: (cellValues) => {
          return (
            <ButtonMui
              variant="contained"
              color="error"
              onClick={(event) => {
                let res = window.confirm("Sei sicuro di voler eliminare il servizio?");
                if (res) {
                  deleteService({variables: { id: cellValues.row.id }})
                }
              }}
            >
              Cancella
            </ButtonMui>
          );
        },
      }
    ];

    const serviceGroups = dataServiceGroups?.["clubmate_clubmateservice_clubmateservicegroup"] || [];

    const handleDeleteGroup = (groupId) => {
      // Controlla se ci sono servizi associati a questo gruppo
      const servicesInGroup = combinedData.filter(service => service.service_group_id === groupId);

      if (servicesInGroup.length > 0) {
        alert(`Non è possibile eliminare questo gruppo perché ci sono ${servicesInGroup.length} servizi associati. Rimuovi prima i servizi dal gruppo.`);
        return;
      }

      let res = window.confirm("Sei sicuro di voler eliminare questo gruppo?");
      if (res) {
        deleteServiceGroup({variables: { id: groupId }});
      }
    };

    const columnsServiceGroups = [
      { field: 'name', headerName: 'Nome Gruppo', width: 300 },
      {
        field: 'actions',
        headerName: 'Azioni',
        width: 120,
        renderCell: (cellValues) => {
          return (
            <ButtonMui
              variant="contained"
              color="warning"
              onClick={() => {
                setSelectedGroup(cellValues.row);
                setGroupName(cellValues.row.name);
                setOpenDialog(true);
              }}
            >
              Modifica
            </ButtonMui>
          );
        },
      },
      {
        field: 'delete',
        headerName: 'Cancella',
        width: 120,
        renderCell: (cellValues) => {
          return (
            <ButtonMui
              variant="contained"
              color="error"
              onClick={() => handleDeleteGroup(cellValues.row.id)}
            >
              Cancella
            </ButtonMui>
          );
        },
      }
    ];

    const handleSaveGroup = () => {
      if (selectedGroup) {
        // Modifica
        updateServiceGroup({
          variables: {
            id: selectedGroup.id,
            name: groupName
          }
        });
      } else {
        // Inserimento
        insertServiceGroup({
          variables: { name: groupName }
        });
      }
      setOpenDialog(false);
      setSelectedGroup(null);
      setGroupName('');
    };

    const handleCloseDialog = () => {
      setOpenDialog(false);
      setSelectedGroup(null);
      setGroupName('');
    };

    return (
      <>
        <Box overflow="auto" height="100%" width="100%" alignSelf='center' style={{marginTop:'40px'}}>
          <div style={{ height: '40vh', width: '100%' }}>
            <DataGrid
              rows={combinedData}
              columns={columnsPack}
              getRowClassName={(params) => {
                // Applica la classe CSS per i servizi archiviati
                return params.row.archive ? 'archived-row' : '';
              }}
              sx={{
                '& .archived-row': {
                  backgroundColor: '#ffebee', // Rosso chiaro
                  '&:hover': {
                    backgroundColor: '#ffcdd2', // Rosso leggermente più scuro al hover
                  },
                },
              }}
            />
          </div>

          <div style={{ marginTop: '40px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Gruppi di Servizi</h3>
            <ButtonMui
              variant="contained"
              onClick={() => {
                setSelectedGroup(null);
                setGroupName('');
                setOpenDialog(true);
              }}
              sx={{ backgroundColor: '#f8b20e', '&:hover': { backgroundColor: '#d99f0c' } }}
            >
              Aggiungi Gruppo
            </ButtonMui>
          </div>

          <div style={{ height: '30vh', width: '100%' }}>
            <DataGrid
              rows={serviceGroups}
              columns={columnsServiceGroups}
            />
          </div>
        </Box>

        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>{selectedGroup ? 'Modifica Gruppo' : 'Aggiungi Gruppo'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nome Gruppo"
              type="text"
              fullWidth
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <ButtonMui onClick={handleCloseDialog} color="secondary">
              Annulla
            </ButtonMui>
            <ButtonMui onClick={handleSaveGroup} color="primary" disabled={!groupName.trim()}>
              Salva
            </ButtonMui>
          </DialogActions>
        </Dialog>
      </>
    )
}

export default Servizi;